import type { RefObject } from 'react'
import type { AggregatedRfqView } from '@/types/api/aggregated/rfq'
import type { RFQUpdateEventMessage } from '@/types/api/events/rfq-update'

import { RFQ_EVENTS_HWM_KEY } from '@/components/trade-desk/utils/use-connect-to-websockets'
import { createLogger } from '@/lib/logger'

const LOGGER = createLogger('utils/trade-blotter/rfq-stream/rfq-update')

import { getMarketDayStart } from '@/utils/dates/dateHelpers'
import { isControlMessage } from '@/utils/trade-blotter/control-message'
import { isEventOutsideRange } from '@/utils/trade-blotter/helpers'
import { updateHwmInLocalStorage } from '@/utils/trade-blotter/web-socket'

export function aggregateRfqEvents(
  rfqEvents: RFQUpdateEventMessage[],
  startTimeMillis: number,
  endTimeMillis?: number,
): AggregatedRfqView[] {
  const aggregatedRfqViewMap: Map<string, AggregatedRfqView> = new Map()
  rfqEvents
    .filter((rfqEvent) => {
      return !isEventOutsideRange(
        rfqEvent?.event?.time,
        startTimeMillis,
        endTimeMillis,
      )
    })
    .forEach((rfqEvent) => {
      const { summary: summaryFromEvent, event } = rfqEvent
      const rfqId = event.rfq_id
      const aggregatedRfqView = aggregatedRfqViewMap.get(rfqId)
      if (!aggregatedRfqView) {
        aggregatedRfqViewMap.set(rfqId, {
          id: rfqId,
          latest_state: summaryFromEvent,
          latest_msg_number: rfqEvent.msg_number,
          events: [rfqEvent],
        })
      } else {
        // Do not allow system updates to clobber the latest_state with undefined
        const summary =
          rfqEvent.msg_type === 'system_update'
            ? aggregatedRfqView.latest_state
            : summaryFromEvent
        aggregatedRfqView.latest_state = summary
        aggregatedRfqView.latest_msg_number = rfqEvent.msg_number
        aggregatedRfqView.events.push(rfqEvent)
      }
    })
  return Array.from(aggregatedRfqViewMap.values()).sort((a, b) => {
    const aMsgNumber = BigInt(a.latest_msg_number || 0)
    const bMsgNumber = BigInt(b.latest_msg_number || 0)
    return Number(aMsgNumber - bMsgNumber)
  })
}

/**
 * Handles a single RFQ-stream websocket message: ignores control frames
 * (heartbeat / connection ack) before reading the event payload, then upserts
 * the RFQ into the aggregated-view ref and advances the stored HWM.
 */
export function handleRfqWebSocketMessage(
  event: MessageEvent,
  aggregatedRfqViewsRef: RefObject<Map<string, AggregatedRfqView>>,
  onRfqUpdateApplied: ({
    toUpdate,
  }: {
    toUpdate?: AggregatedRfqView[]
  }) => void,
  shouldRemove?: (view: AggregatedRfqView) => boolean,
) {
  // Process each event in the order they are received, which
  // is also in ascending order by msg_number
  const rfqEvent: RFQUpdateEventMessage = JSON.parse(event.data)

  // Control frames (heartbeat / connection ack) carry no event payload — ignore
  // them before reading event/HWM fields (reading event.rfq_id on an event-less
  // heartbeat would otherwise throw).
  if (isControlMessage(rfqEvent)) {
    return
  }

  LOGGER.info('RFQ WebSocket event received', {
    browserTime: new Date().toISOString(),
    eventId: rfqEvent.event_id,
    eventTime: rfqEvent.event?.time,
    eventType: rfqEvent.event?.event_type,
    msgNumber: rfqEvent.msg_number,
    msgType: rfqEvent.msg_type,
    orderId:
      rfqEvent.event && 'order_id' in rfqEvent.event
        ? rfqEvent.event.order_id
        : undefined,
    rfqId: rfqEvent.event?.rfq_id,
  })

  const todayStart = getMarketDayStart()
  const isOutsideRange = isEventOutsideRange(rfqEvent?.event?.time, todayStart)

  if (!isOutsideRange) {
    const rfqId = rfqEvent.event.rfq_id
    const map = aggregatedRfqViewsRef.current
    const existingRfqView = map.get(rfqId) || null
    if (!existingRfqView) {
      map.set(rfqId, {
        id: rfqId,
        latest_state: rfqEvent.summary,
        latest_msg_number: rfqEvent.msg_number,
        events: [rfqEvent],
      })
    } else {
      // Do not allow system updates to clobber the latest_state with undefined
      const summary =
        rfqEvent.msg_type === 'system_update'
          ? existingRfqView.latest_state
          : rfqEvent.summary
      const updatedRfqView = {
        ...existingRfqView,
        latest_state: summary,
        latest_msg_number: rfqEvent.msg_number,
        events: [...existingRfqView.events, rfqEvent],
      }
      map.set(rfqId, updatedRfqView)
    }
    if (shouldRemove) {
      map.entries().forEach(([key, value]) => {
        if (shouldRemove(value)) {
          map.delete(key)
        }
      })
    }
    const updatedRfqView = map.get(rfqId)
    const toUpdate = updatedRfqView ? [updatedRfqView] : undefined
    if (toUpdate) {
      onRfqUpdateApplied({ toUpdate })
    }
  }
  updateHwmInLocalStorage(rfqEvent.msg_number, RFQ_EVENTS_HWM_KEY)
}
